import {
  cryptorsAdminControllerCreateOne,
  cryptorsAdminControllerGetOne,
  cryptorsAdminControllerList,
  cryptorsAdminControllerUpdateOne,
  type CreateCryptor,
  type Cryptor,
  type CryptorsAdmin,
  type UpdateCryptor,
  unwrapApiResponse,
} from "@cryptors/api-client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

const getCryptors = async (): Promise<CryptorsAdmin> => {
  const data = await unwrapApiResponse(
    cryptorsAdminControllerList<true>({
      query: { limit: 500 },
      throwOnError: true,
    }),
  );
  return Array.isArray(data)
    ? data
    : ((data as { data: CryptorsAdmin }).data ?? []);
};

export const useGetCryptors = () => {
  return useQuery({
    initialData: [],
    queryKey: ["cryptors"],
    queryFn: () => getCryptors(),
  });
};

const getCryptor = async (cryptorId: number): Promise<Cryptor> =>
  unwrapApiResponse(
    cryptorsAdminControllerGetOne<true>({
      path: { id: String(cryptorId) },
      throwOnError: true,
    }),
  );

export const useGetCryptor = (cryptorId: number) => {
  return useQuery({
    queryKey: ["cryptors", cryptorId],
    queryFn: () => getCryptor(cryptorId),
  });
};

const postCryptor = async (data: CreateCryptor) =>
  unwrapApiResponse(
    cryptorsAdminControllerCreateOne<true>({
      body: data,
      throwOnError: true,
    }),
  );

export const usePostCryptor = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: postCryptor,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cryptors"] });
    },
  });
};

const patchCryptor = async ({
  id,
  ...data
}: {
  id: number;
} & UpdateCryptor) =>
  unwrapApiResponse(
    cryptorsAdminControllerUpdateOne<true>({
      body: data,
      path: { id: String(id) },
      throwOnError: true,
    }),
  );

export const usePatchCryptor = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: patchCryptor,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cryptors"] });
    },
  });
};
